// router.js

import React, { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";

// pages here
import Index from "./pages/index";
import { MaintenancePage } from "custom-components-v1";
import TestAnalysisDemo from "../../../packages/ui/src/TestAnalysisDemo/TestAnalysisDemo.jsx";
import TestAnalysisDemoNeet from "../../../packages/ui/src/TestAnalysisDemo/TestAnalysisDemoNeet.jsx";
import TestAnalysisDemoBitsat from "../../../packages/ui/src/TestAnalysisDemo/TestAnalysisDemoBitsat.jsx";
import axios from "axios";

const Dashboard = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.Dashboard })));
const ErrorPage = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.ErrorPage })));
const UnlockedTestDetails = lazy(() =>
  import("custom-components-v1").then((mod) => ({ default: mod.UnlockedTestDetails }))
);
const UnlockedTestListing = lazy(() =>
  import("custom-components-v1").then((mod) => ({ default: mod.UnlockedTestListing }))
);
const TestSubmission = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.TestSubmission })));
const MyMistakes = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.MyMistakes })));
const SolutionsV3 = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.SolutionsV3 })));
const ChapterWiseMistakesDetailed = lazy(() =>
  import("custom-components-v1").then((mod) => ({ default: mod.ChapterWiseMistakesDetailed }))
);
const TestWiseMistakesDetailed = lazy(() =>
  import("custom-components-v1").then((mod) => ({ default: mod.TestWiseMistakesDetailed }))
);
const Resources = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.Resources })));
const Lectures = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.Lectures })));
const NotebookFinal = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.NotebookFinal })));
const ProfileFinal = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.ProfileFinal })));
const TestAnalysisV3 = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.TestAnalysisV3 })));
const TestAnalysisCombined = lazy(() =>
  import("custom-components-v1").then((mod) => ({ default: mod.TestAnalysisCombined }))
);
const Leaderboard = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.Leaderboard })));
const PackAnalysisFinal = lazy(() =>
  import("custom-components-v1").then((mod) => ({ default: mod.PackAnalysisFinal }))
);
const ExploreQuizrr = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.ExploreQuizrr })));
const Challenge99Landing = lazy(() =>
  import("custom-components-v1").then((mod) => ({ default: mod.Challenge99Landing }))
);
const Challenge99Home = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.Challenge99Home })));
const ChallengeInternalGateSheet = lazy(() =>
  import("custom-components-v1").then((mod) => ({ default: mod.ChallengeInternalGateSheet }))
);
const TimelineView = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.TimelineView })));

//Auth Components v3
const RegisterForm = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.RegisterForm })));
const RegisterVerify = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.RegisterVerify })));
const Login = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.Login })));
const ResetRequest = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.ResetRequest })));
const ResetConfirm = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.ResetConfirm })));
// const Auth = lazy(() => import("custom-components-v1").then((mod) => ({ default: mod.Auth })));

const Test = lazy(() => import("./pages/packs/v2-Test-Analysis/test"));
const TestDemo = lazy(() => import("./pages/packs/v2-Test-Analysis-Demo/test"));
const JeeMainAnalysis = lazy(() => import("./pages/packs/jee-main-analysis/test"));

// Auth Routes
const AuthAdmin = lazy(() => import("./pages/auth/admin"));
const Logout = lazy(() => import("./pages/auth/logout"));

// Test Routes
const TestUI = lazy(() => import("./pages/tests"));
const Inst = lazy(() => import("./pages/tests/instructions"));
const Sub = lazy(() => import("./pages/tests/submission"));

const AnalysisDemoRouter = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const exam = searchParams.get("exam")?.toLowerCase() || "jee";

  switch (exam) {
    case "neet":
      return <TestAnalysisDemoNeet />;
    case "bitsat":
      return <TestAnalysisDemoBitsat />;
    case "jee":
    default:
      return <TestAnalysisDemo />;
  }
};

const UP = JSON.parse(localStorage.getItem("userProfile"));

// Soft-launch gate: mirrors backend-migration's middlewares/challengeInternalAccess.js - when
// platformSettings.challengeInternalOnly is on, only @mathongo.com accounts can reach the challenge
// routes at all, so someone with a bookmarked/typed-in /challenge URL gets bounced back to the
// dashboard instead of hitting the server's 403 on every API call the page makes.
const canAccessChallenge = (platformSettings) =>
  !platformSettings?.challengeInternalOnly || Boolean(UP?.email?.toLowerCase().endsWith("@mathongo.com"));

// Rendered in place of the challenge route for a blocked user - the gate sheet itself (centered modal
// on desktop, bottom sheet on mobile, see ChallengeInternalGateSheet) is the whole page; its only
// action sends them back to the dashboard.
const ChallengeAccessGate = () => {
  const navigate = useNavigate();
  return <ChallengeInternalGateSheet open onClose={() => navigate("/v2/home", { replace: true })} />;
};

const Router = () => {
  const [platformSettings, setPlatformSettings] = useState(null);

  useEffect(() => {
    const getPlatformSettings = async () => {
      const res = await axios.get("/api/platform-settings");
      if (res.status === 200) {
        setPlatformSettings(res.data.data);
      }
    };
    getPlatformSettings();
  }, []);

  const challengeAllowed = canAccessChallenge(platformSettings);

  return platformSettings?.maintenanceMode ? (
    <MaintenancePage />
  ) : (
    <Suspense fallback={<div style={{ height: "100vh" }}></div>}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/v3/home" element={<Navigate to="/v2/home" replace />} />
        <Route path="/auth/admin" element={<AuthAdmin />} />
        <Route path="/v3/:packId/timelineview" element={<TimelineView />} />
        {/* New v3 Auth Routes */}
        <Route path="/auth" element={<Navigate to="/auth/login" replace />} />
        <Route path="/auth/login" element={<Login />} />
        <Route path="/auth/register" element={<RegisterForm />} />
        <Route path="/auth/verifyEmail" element={<RegisterVerify />} />
        <Route path="/auth/reset/request" element={<ResetRequest />} />
        <Route path="/auth/reset/confirm" element={<ResetConfirm />} />
        <Route path="/logout" element={<Logout />} />
        {/* Revamp New pages */}
        <Route path="/v2/home" element={<Dashboard />} />
        <Route path="/v3/:packId/tests" element={<UnlockedTestListing />} />
        <Route path="/v3/:packId/resources" element={<Resources />} />
        <Route path="/v3/:packId/lectures" element={<Lectures />} />
        <Route path="/:packId/:testId/details" element={<UnlockedTestDetails />} />
        <Route path="/:packId/:testId/solutions" element={<SolutionsV3 />} />
        <Route path="/:packId/:testId/analysis" element={<TestAnalysisV3 />} />
        <Route path="/:packId/:testIdOne/:testIdTwo/analysis" element={<TestAnalysisCombined />} />
        <Route path="/v3/:packId/:testId/leaderboard" element={<Leaderboard />} />
        {/* Updated My Mistakes Routes */}
        <Route path="/v3/my-mistakes" element={<MyMistakes />} />
        <Route path="/v3/my-mistakes/test/:testId" element={<TestWiseMistakesDetailed />} />
        <Route path="/v3/my-mistakes/chapter/:chapterId" element={<ChapterWiseMistakesDetailed />} />
        <Route path="/tests/:pack/:test/submission" element={<TestSubmission />} />
        {/* <Route path="/tests/usersignup" element={<UserSignup />} /> */}
        <Route path="/tests/:pack/:test/instructions" element={<Inst />} />
        <Route path="/tests/:pack/:test" element={<TestUI />} />
        {/* <Route path="/analysis-demo" element={<TestDemo />} /> */}
        <Route path="/jee-main-analysis" element={<JeeMainAnalysis />} />
        <Route path="/v3/notebooks" element={<NotebookFinal />} />
        <Route path="/v3/profile" element={<ProfileFinal />} />
        {/* Pack Analysis Routes */}
        <Route path="/v3/pack-analysis" element={<Navigate to="/v3/pack-analysis/performance" replace />} />
        <Route path="/v3/pack-analysis/performance" element={<PackAnalysisFinal />} />
        <Route path="/v3/pack-analysis/timeline" element={<PackAnalysisFinal />} />
        <Route path="/v3/pack-analysis/qs-type-breakup" element={<PackAnalysisFinal />} />
        <Route path="/v3/pack-analysis/quality-of-attempt" element={<PackAnalysisFinal />} />
        <Route path="/v3/pack-analysis/time-analysis" element={<PackAnalysisFinal />} />
        <Route path="/v3/pack-analysis/difficulty-analysis" element={<PackAnalysisFinal />} />
        <Route path="/v3/pack-analysis/chapter-analysis" element={<PackAnalysisFinal />} />
        <Route path="/v3/explore" element={<ExploreQuizrr />} />
        <Route path="/challenge" element={challengeAllowed ? <Challenge99Landing /> : <ChallengeAccessGate />} />
        <Route path="/challenge/home" element={challengeAllowed ? <Challenge99Home /> : <ChallengeAccessGate />} />
        <Route path="/analysis-demo" element={<AnalysisDemoRouter />} />
        <Route path="*" element={<ErrorPage errorCode={404} />} />
      </Routes>
    </Suspense>
  );
};

export default Router;
